import { useState, useCallback } from 'react';

import { useNavigationService } from '../../../../services/navigate-service';
import { OrderSliderForms } from '../model/CartModel';
import {
  PropertyCartItem,
  AssetCartItem,
  DeleteConfirmation,
  SliderState,
  AssetEditModalState,
  CheckoutData
} from '../types/cart.types';
import type { GroupedAssetRow } from '../utils/cartTransforms';
import { ROUTES } from '../../../../config/routes';

const INITIAL_DELETE_STATE: DeleteConfirmation = {
  show: false,
  itemType: 'property',
  itemId: null,
  itemLabel: ''
};

type UseCartPageActionsParams = {
  // Cart actions
  removePropertyItem: (propertyId: string) => Promise<void>;
  removeAssetItem: (lineItemUUID: string | undefined, assetId: string, slug: string) => Promise<void>;
  clearCart: () => Promise<void>;
  updatePropertyItem: (data: PropertyCartItem) => Promise<void>;

  // Cart data
  propertyItems: PropertyCartItem[];
  assetItems: AssetCartItem[];
  cartId: string;
  totalInitialPrice: number;
  totalSavingPrice: number;
  totalPrice: number;
  /** For order confirmation's free floor plan line (DV-3808). */
  freeFloorPlanImage: string | null;
};

export const useCartPageActions = ({
  removePropertyItem,
  removeAssetItem,
  clearCart,
  updatePropertyItem,
  propertyItems,
  assetItems,
  cartId,
  totalInitialPrice,
  totalSavingPrice,
  totalPrice,
  freeFloorPlanImage
}: UseCartPageActionsParams) => {
  const { navigateTo } = useNavigationService();

  const [sliderState, setSliderState] = useState<SliderState>({
    show: false,
    type: OrderSliderForms.SERVICES,
    data: null
  });
  const [assetEditModal, setAssetEditModal] = useState<AssetEditModalState>({
    show: false,
    item: null
  });
  const [deleteConfirmation, setDeleteConfirmation] =
    useState<DeleteConfirmation>(INITIAL_DELETE_STATE);
  const [showPartialOrderModal, setShowPartialOrderModal] = useState(false);
  const [emptyServiceCount, setEmptyServiceCount] = useState(0);

  // --- Property table handlers ---
  const handleEditService = useCallback((item: PropertyCartItem) => {
    setSliderState({
      show: true,
      type: OrderSliderForms.SERVICES,
      data: item
    });
  }, []);

  const handleEditAccess = useCallback((item: PropertyCartItem) => {
    setSliderState({
      show: true,
      type: OrderSliderForms.ACCESSINFO,
      data: item
    });
  }, []);

  const handleDeleteProperty = useCallback((item: PropertyCartItem) => {
    setDeleteConfirmation({
      show: true,
      itemType: 'property',
      itemId: item.propertyId,
      itemLabel: item.formattedAddress || 'this property'
    });
  }, []);

  // --- Asset table handlers ---
  // Accept GroupedAssetRow (editable cart) or plain AssetCartItem (readOnly).
  // GroupedAssetRow extends AssetCartItem, so a plain item is also valid here.
  const handleEditAsset = useCallback((row: GroupedAssetRow) => {
    // Use the first underlying asset as the representative item for the modal
    // (drives the image preview and initial description).
    setAssetEditModal({ show: true, item: row });
  }, []);

  const handleDeleteAsset = useCallback((row: GroupedAssetRow) => {
    // DV-2131 §3: identify the line by its stable lineItemUUID. Keep the (assetId, slug) composite
    // as a legacy fallback — assetId (not assetRefs[0]) stays stable after a first-photo removal.
    const lineAssetId = row.assetId ?? row.assetRefs?.[0]?.assetId ?? '';
    setDeleteConfirmation({
      show: true,
      itemType: 'asset',
      itemId: lineAssetId,
      itemSlug: row.service?.shoppingCartSlug,
      lineItemUUID: row.lineItemUUID,
      itemLabel: row.service?.name || 'this image edit',
    });
  }, []);

  // --- Clear cart ---
  const handleClearCart = useCallback(() => {
    setDeleteConfirmation({
      show: true,
      itemType: 'all',
      itemId: null,
      itemLabel: 'all items'
    });
  }, []);

  // --- Delete confirmation ---
  const handleConfirmDelete = useCallback(async () => {
    // Close the dialog immediately — the cart mutations are optimistic (local state updates up
    // front and rolls back + surfaces an error on failure), so there's no reason to hold the
    // dialog open with a spinner for the network round-trip.
    const confirmation = deleteConfirmation;
    setDeleteConfirmation(INITIAL_DELETE_STATE);
    try {
      if (confirmation.itemType === 'all') {
        await clearCart();
      } else if (confirmation.itemType === 'property' && confirmation.itemId) {
        await removePropertyItem(confirmation.itemId);
      } else if (
        confirmation.itemType === 'asset' &&
        confirmation.itemId &&
        confirmation.itemSlug
      ) {
        await removeAssetItem(
          confirmation.lineItemUUID,
          confirmation.itemId,
          confirmation.itemSlug,
        );
      }
    } catch {
      // The cart hook already rolls back local state and sets the error message on failure.
    }
  }, [deleteConfirmation, clearCart, removePropertyItem, removeAssetItem]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmation(INITIAL_DELETE_STATE);
  }, []);

  // --- Slider handlers ---
  const handleSliderClose = useCallback(() => {
    setSliderState({
      show: false,
      type: OrderSliderForms.SERVICES,
      data: null
    });
    sessionStorage.removeItem('selectedServiceOnCart');
  }, []);

  const handleSliderContinueToAccess = useCallback(
    (data: PropertyCartItem) => {
      setSliderState({ show: true, type: OrderSliderForms.ACCESSINFO, data });
    },
    []
  );

  const handleSliderBackToService = useCallback((data: PropertyCartItem) => {
    setSliderState({ show: true, type: OrderSliderForms.SERVICES, data });
  }, []);

  const handleSliderDone = useCallback(
    async (data: PropertyCartItem) => {
      await updatePropertyItem(data);
      handleSliderClose();
    },
    [updatePropertyItem, handleSliderClose]
  );

  // --- Asset edit modal ---
  const handleAssetEditClose = useCallback(() => {
    setAssetEditModal({ show: false, item: null });
  }, []);

  // --- Checkout ---
  const getEmptyServiceCount = useCallback(() => {
    return propertyItems.filter(
      (item) => !item.services || item.services.length === 0
    ).length;
  }, [propertyItems]);

  const navigateToPayment = useCallback(() => {
    const checkoutData: CheckoutData = {
      orderData: propertyItems,
      editPhotoOrders: assetItems,
      isCartOrder: true,
      cartId,
      serviceTotalPrice: totalInitialPrice,
      totalSavingPrice,
      orderTotalPrice: totalPrice,
      freeFloorPlanImage
    };
    navigateTo(ROUTES.ORDERS_CART_PAYMENT, {}, checkoutData);
  }, [
    propertyItems,
    assetItems,
    cartId,
    totalInitialPrice,
    totalSavingPrice,
    totalPrice,
    freeFloorPlanImage,
    navigateTo
  ]);

  const handleCheckout = useCallback(() => {
    const emptyCount = getEmptyServiceCount();
    if (emptyCount > 0) {
      setEmptyServiceCount(emptyCount);
      setShowPartialOrderModal(true);
    } else {
      navigateToPayment();
    }
  }, [getEmptyServiceCount, navigateToPayment]);

  const handlePartialOrderContinue = useCallback(() => {
    setShowPartialOrderModal(false);
    navigateToPayment();
  }, [navigateToPayment]);

  const handlePartialOrderClose = useCallback(() => {
    setShowPartialOrderModal(false);
  }, []);

  const hasAssetItems = assetItems.length > 0;
  const hasPropertyItems = propertyItems.length > 0;
  const hasAnyItems = hasPropertyItems || hasAssetItems;

  const isCheckoutDisabled = hasAssetItems
    ? false
    : !hasAnyItems ||
      (hasPropertyItems && getEmptyServiceCount() === propertyItems.length);

  return {
    // State
    sliderState,
    assetEditModal,
    deleteConfirmation,
    showPartialOrderModal,
    emptyServiceCount,

    // Property handlers
    handleEditService,
    handleEditAccess,
    handleDeleteProperty,

    // Asset handlers
    handleEditAsset,
    handleDeleteAsset,

    // Cart-level handlers
    handleClearCart,
    handleConfirmDelete,
    handleCancelDelete,

    // Slider handlers
    handleSliderClose,
    handleSliderContinueToAccess,
    handleSliderBackToService,
    handleSliderDone,

    // Asset edit modal handlers
    handleAssetEditClose,

    // Checkout
    handleCheckout,
    navigateToPayment,
    getEmptyServiceCount,
    isCheckoutDisabled,
    handlePartialOrderContinue,
    handlePartialOrderClose,

    // Navigation
    navigateTo
  };
};
